// Campaign history — every campaign the tenant has run, counts derived from
// recipient rows (never the stale aggregate). Click through to per-recipient
// detail. Partial/failed sends are shown, not hidden — the history is honest.
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../apiClient';
import CampaignTracker from './CampaignTracker';
import './CampaignsList.css';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const COLS = [
  { k: 'total', label: 'Recipients', cls: '' },
  { k: 'sent', label: 'Sent', cls: 'ok' },
  { k: 'delivered', label: 'Delivered', cls: 'ok' },
  { k: 'replied', label: 'Replied', cls: 'reply' },
  { k: 'opted_out', label: 'Opted out', cls: 'neg' },
  { k: 'failed', label: 'Failed', cls: 'neg' },
  { k: 'scheduled_followups', label: 'Follow-ups', cls: 'warn' },
  // Mailgun engagement (email campaigns; blank for SMS)
  { k: 'eng_delivered', label: 'Delivered ✓', cls: 'ok', eng: 'delivered' },
  { k: 'eng_opened', label: 'Opened', cls: 'reply', eng: 'opened' },
  { k: 'eng_clicked', label: 'Clicked', cls: 'reply', eng: 'clicked' },
  { k: 'eng_bounced', label: 'Bounced', cls: 'neg', eng: 'bounced' },
];

const ACTIVE = new Set(['approved', 'sending', 'queued', 'pending']);

// A send that ended failed/cancelled but reached people, or completed with
// some failures, is partial — not a clean success and not a total loss.
const statusLabel = (c) => {
  const st = (c.status || '').toLowerCase();
  if ((st === 'failed' || st === 'cancelled') && c.sent > 0) return 'partial';
  if (!ACTIVE.has(st) && c.failed > 0 && c.sent > 0) return 'partial';
  return st || 'unknown';
};

export default function CampaignsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/customers/outreach/campaigns/');
      setRows(Array.isArray(r.data?.campaigns) ? r.data.campaigns : []);
      setError('');
    } catch (e) {
      setError('Could not load campaigns.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="cml-page">
      <div className="cml-head">
        <h2>Campaigns</h2>
        <p>Every campaign you've run · counts come from the recipient rows</p>
      </div>

      {error ? (
        <div className="cml-error">{error} <button className="cml-retry" onClick={load}>Retry</button></div>
      ) : loading ? (
        <div className="cml-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="cml-empty">No campaigns yet. When you send outreach, it shows up here.</div>
      ) : (
        <div className="cml-table-wrap">
          <table className="cml-table">
            <thead>
              <tr>
                <th className="cml-l">Campaign</th>
                <th className="cml-l">Audience</th>
                {COLS.map((col) => <th key={col.k}>{col.label}</th>)}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const st = statusLabel(c);
                return (
                  <tr key={c.id} className="cml-row" onClick={() => setOpenId(c.id)}>
                    <td className="cml-l">
                      <button className="cml-name" onClick={(e) => { e.stopPropagation(); setOpenId(c.id); }}>
                        {c.name || 'Campaign'}
                      </button>
                      <div className="cml-date">{fmtDate(c.created_at)} · {c.channel}</div>
                    </td>
                    <td className="cml-l cml-aud">{c.audience || '—'}</td>
                    {COLS.map((col) => (
                      <td key={col.k} className={`cml-num cml-${col.cls}`}>{col.eng ? (c.engagement ? (c.engagement[col.eng] ?? 0) : '—') : (c[col.k] ?? 0)}</td>
                    ))}
                    <td><span className={`cml-badge cml-badge-${st}`}>{st}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openId && <CampaignTracker campaignId={openId} onClose={() => { setOpenId(null); load(); }} />}
    </div>
  );
}
